import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Music } from 'lucide-react';
import { motion } from 'framer-motion';

export const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signIn(email.trim(), password.trim());
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'message' in err ? String(err.message) : '';
      if (errorMessage.includes('Invalid login credentials')) {
        setError('The email or password you entered is incorrect');
      } else if (errorMessage.includes('Email not confirmed')) {
        setError('Please verify your email address before signing in');
      } else {
        setError('An error occurred while signing in. Please try again.');
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-royal-gold via-royal-gold-light to-royal-gold-dark" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-white/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-black/10 rounded-full blur-[80px]" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Music className="w-8 h-8" />
            <span className="text-2xl font-bold">VISION</span>
          </Link>
          <h1 className="text-5xl font-bold leading-tight mb-6">
            Welcome Back to VISION
          </h1>
          <p className="text-xl text-white/90 max-w-md">
            Sell music direct to fans, get paid daily, and own your data.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-stone-50">
        <div className="max-w-md w-full">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-royal-text-secondary hover:text-royal-text transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="lg:hidden flex items-center gap-2 mb-8">
              <Music className="w-8 h-8 text-royal-gold" />
              <span className="text-2xl font-bold text-royal-text">VISION</span>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-royal-text mb-2">Welcome</h2>
              <p className="text-royal-text-secondary">Sign in to your account or create a new one.</p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-red-600 text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    className="input-royal"
                    placeholder="fan@visionmusic.co"
                  />
                </div>

                <div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    className="input-royal"
                    placeholder="Password"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-royal-gold hover:text-royal-gold-dark transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-royal-primary w-full justify-center"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Sign In'
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stone-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-stone-50 text-royal-text-secondary">or</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-base font-medium text-royal-text bg-white border border-stone-200 hover:bg-stone-50 transition-all shadow-sm"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>

                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-base font-medium text-royal-text bg-white border border-stone-200 hover:bg-stone-50 transition-all shadow-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Continue with Apple
                </button>
              </div>
            </form>

            <p className="text-center text-xs text-royal-text-secondary">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-royal-gold hover:text-royal-gold-dark">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-royal-gold hover:text-royal-gold-dark">Privacy Policy</Link>.
              <br />
              You may receive SMS notifications from us and can opt out at any time.
            </p>

            <div className="text-center">
              <span className="text-royal-text-secondary">Don't have an account?</span>{' '}
              <Link to="/signup" className="font-semibold text-royal-gold hover:text-royal-gold-dark transition-colors">
                Sign up
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
